<script setup>
  import { shallowRef, defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { useRoute } from "vue-router";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { pascalCase } from "change-case";
  import { useSurveySetupStore } from "@stores/survey/setup/surveySetup";

  const route = useRoute();
  const components = shallowRef({});

  const surveySetupStore = useSurveySetupStore();
  const { currentSections, activeItems } = storeToRefs(surveySetupStore);

  const openSection = ref("general");

  const determineComponent = (name) =>
    defineAsyncComponent(() => import(`../../components/Survey/Setup/Sections/${pascalCase(name)}SetupSection.vue`));
  const determineComponents = () => {
    components.value = {};
    currentSections.value.forEach((section) => {
      components.value[section.name] = determineComponent(section.name);
    });
  };
  const resetActiveItems = () => {
    activeItems.value = ["general"];
  };

  const applyHashSection = () => {
    const hash = route.hash?.replace(/^#/, "");
    if (!hash) return;
    const match = currentSections.value.find((s) => s.name === hash);
    if (match) openSection.value = match.name;
  };

  onMounted(() => {
    determineComponents();
    applyHashSection();
  });
  onUnmounted(() => resetActiveItems());
</script>

<template>
  <ThreePanePage
    :span-center="6"
    data-cy="survey-setup-page">
    <template #center>
      <Card class="p-6">
        <CardHeader>
          <CardTitle class="text-2xl">Setup</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            v-model="openSection"
            type="single"
            collapsible>
            <AccordionItem
              v-for="section in currentSections"
              :key="`setup-section-${section.name}`"
              :value="section.name">
              <AccordionTrigger class="text-base font-semibold">{{ section.label }}</AccordionTrigger>
              <AccordionContent>
                <component :is="components[section.name]" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
